import React from 'react';
import { AlertTriangle } from 'lucide-react';

const NonComplianceCasesPanel = ({ cases }) => {
  const getSeverityBadge = (severity) => {
    const severityConfig = {
      critical: { bg: 'bg-red-100', text: 'text-red-800', label: 'Critical' },
      high: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'High' },
      medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Medium' },
      low: { bg: 'bg-green-100', text: 'text-green-800', label: 'Low' }
    };

    const config = severityConfig?.[severity] || severityConfig?.medium;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        {config?.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!cases || cases?.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-red-200">
      <div className="px-6 py-4 bg-red-50 border-b border-red-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-6 w-6 text-red-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Non-Compliance Cases</h2>
              <p className="text-sm text-gray-600">{cases?.length} active cases requiring attention</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="space-y-4">
          {cases?.map((complianceCase) => (
            <div
              key={complianceCase?.id}
              className="border border-red-200 rounded-lg p-4 bg-red-50"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-red-100 text-red-800">
                    {complianceCase?.caseNumber}
                  </span>
                  {getSeverityBadge(complianceCase?.caseSeverity)}
                </div>
                <span className="text-xs text-gray-500">
                  {formatDate(complianceCase?.violationDate)}
                </span>
              </div>

              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {complianceCase?.employeeName} ({complianceCase?.employeeNumber})
              </h3>
              <p className="text-sm text-gray-700 mb-3">
                {complianceCase?.violationDescription}
              </p>

              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Department</p>
                  <p className="text-sm font-medium text-gray-900">{complianceCase?.department}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Days Overdue</p>
                  <p className="text-sm font-medium text-red-600">{complianceCase?.daysOverdue} days</p>
                </div>
              </div>

              {complianceCase?.businessImpact && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-gray-700 mb-1">Business Impact:</p>
                  <p className="text-sm text-gray-600">{complianceCase?.businessImpact}</p>
                </div>
              )}

              {complianceCase?.legalRisk && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-gray-700 mb-1">Legal Risk:</p>
                  <p className="text-sm text-gray-600">{complianceCase?.legalRisk}</p>
                </div>
              )}

              {complianceCase?.actionsTaken && complianceCase?.actionsTaken?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-red-200">
                  <p className="text-xs font-medium text-gray-700 mb-2">Actions Taken:</p>
                  <div className="space-y-1">
                    {complianceCase?.actionsTaken?.map((action, index) => (
                      <div key={index} className="flex items-start space-x-2 text-xs text-gray-600">
                        <div className="w-1.5 h-1.5 bg-red-500 rounded-full mt-1.5"></div>
                        <div>
                          <span className="font-medium">{action?.action}:</span> {action?.details}
                          {action?.timestamp && (
                            <span className="text-gray-500 ml-1">({formatDate(action?.timestamp)})</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NonComplianceCasesPanel;